import { useEffect, useState } from 'react'
import { Plus, Trash2 } from 'lucide-react'
import Modal from '@/components/common/Modal'
import BusinessLogicFields from './BusinessLogicFields'
import type {
  ItemChange, ItemChangeBusinessLogic, ItemChangeRestApi,
  BusinessLogicValidation, RestApiValidation, RestApiParam, ValueSource,
} from './types'

// Editor for the non-rule item_change modes — Business Logic and Call API.
// These share the exact data shapes used by validations (BusinessLogicValidation
// / RestApiValidation); the function / endpoint returns the item_change result
// contract (set values by column name, (un)protect, multi-form, multi-row).
// (The Rule mode is edited by ItemChangeRuleEditor; expression fills live there.)

interface Props {
  open: boolean
  onClose: () => void
  /** Current item_change — seeds the form when it's business_logic / rest_api. */
  value: ItemChange | null
  /** Which mode to open in when ``value`` isn't already BL/API. */
  initialMode?: 'business_logic' | 'rest_api'
  formColumnNames: string[]
  headerColumnNames?: string[]
  onSave: (ic: ItemChangeBusinessLogic | ItemChangeRestApi) => void
  /** Deep-link a business_logic function to the code editor. */
  onOpenCode?: (method: string) => void
  /** Modal title. Defaults to the item_change wording; pass a generic title
   *  (e.g. "Business Logic") when this edits an action / hook / cross-update. */
  title?: string
  /** Help line under the mode toggle. Defaults to the item_change wording. */
  helpText?: React.ReactNode
  /** Project folder — enables the IOFlow API picker on the Call API form. */
  project?: string
}

const inputStyle: React.CSSProperties = {
  width: '100%', padding: '7px 10px', background: 'var(--color-input-bg)',
  border: '1px solid var(--color-border-strong)', borderRadius: 6,
  color: 'var(--color-text)', fontSize: '0.9125rem', minHeight: 36, boxSizing: 'border-box',
}
const labelStyle: React.CSSProperties = {
  display: 'block', fontSize: '0.8125rem', color: 'var(--color-text-secondary)',
  fontWeight: 600,
  marginBottom: 4, marginTop: 12,
}
const rowStyle: React.CSSProperties = { display: 'flex', gap: 8, alignItems: 'flex-start' }
const helpStyle: React.CSSProperties = {
  fontSize: '0.8125rem', color: 'var(--color-text-secondary)', marginTop: 6, fontStyle: 'italic',
}
const addBtnStyle: React.CSSProperties = {
  display: 'flex', alignItems: 'center', gap: 4, padding: '4px 8px',
  fontSize: '0.8125rem', color: 'var(--color-accent)', background: 'none',
  border: '1px dashed var(--color-border)', borderRadius: 4, cursor: 'pointer', marginTop: 6,
}
const sectionTitleStyle: React.CSSProperties = {
  fontSize: '0.8625rem', fontWeight: 600, color: 'var(--color-accent)',
  marginTop: 14, marginBottom: 6, paddingBottom: 4, borderBottom: '1px solid var(--color-border)',
}
function modeBtn(active: boolean): React.CSSProperties {
  return {
    padding: '5px 12px', fontSize: '0.8425rem', border: '1px solid var(--color-border)',
    background: active ? 'var(--color-accent)' : 'var(--color-bg)',
    color: active ? '#fff' : 'var(--color-text)', cursor: 'pointer',
  }
}

const EMPTY_BL: BusinessLogicValidation = { method: '', description: '' }
const EMPTY_API: RestApiValidation = { url: '', method: 'POST', on_error: 'allow', params: [] }

// A small source(form/header/const) + value control for an API param.
function ParamValueControl({
  source, value, formColumnNames, headerColumnNames, onChange,
}: {
  source: ValueSource
  value: string
  formColumnNames: string[]
  headerColumnNames: string[]
  onChange: (source: ValueSource, value: string) => void
}) {
  const cols = source === 'form' ? formColumnNames : headerColumnNames
  return (
    <div style={{ display: 'flex', gap: 6, flex: 1 }}>
      <select value={source} onChange={(e) => onChange(e.target.value as ValueSource, '')}
        style={{ ...inputStyle, maxWidth: 130, cursor: 'pointer' }}>
        <option value="const">Constant</option>
        <option value="form">Form column</option>
        <option value="header">Header column</option>
      </select>
      {source === 'const' ? (
        <input style={inputStyle} value={value} placeholder="value"
          onChange={(e) => onChange(source, e.target.value)} />
      ) : (
        <select style={{ ...inputStyle, cursor: 'pointer' }} value={value}
          onChange={(e) => onChange(source, e.target.value)}>
          <option value="">— select column —</option>
          {cols.map((c) => <option key={c} value={c}>{c}</option>)}
        </select>
      )}
    </div>
  )
}

export default function ItemChangeCodeEditor({
  open, onClose, value, initialMode = 'business_logic',
  formColumnNames, headerColumnNames = [], onSave, onOpenCode, title, helpText,
  project,
}: Props) {
  const [mode, setMode] = useState<'business_logic' | 'rest_api'>(initialMode)
  const [bl, setBl] = useState<BusinessLogicValidation>(EMPTY_BL)
  const [api, setApi] = useState<RestApiValidation>(EMPTY_API)
  // Active IOFlow APIs from the project's Integration design (Advanced →
  // Events & APIs) — offered as routing targets instead of a raw URL.
  const [ioflowApis, setIoflowApis] = useState<string[]>([])
  useEffect(() => {
    if (!open || !project) return
    let alive = true
    import('@/api/integration').then(({ getIntegrationDesign }) =>
      getIntegrationDesign(project).then((d) => {
        if (alive) setIoflowApis((d.apis || []).filter((a) => a.active !== false).map((a) => a.api_name))
      }),
    ).catch(() => { /* no design yet — picker stays empty */ })
    return () => { alive = false }
  }, [open, project])

  useEffect(() => {
    if (!open) return
    if (value && value.mode === 'business_logic') {
      setMode('business_logic')
      setBl({ ...EMPTY_BL, ...value.business_logic })
      setApi(EMPTY_API)
    } else if (value && value.mode === 'rest_api') {
      setMode('rest_api')
      setApi({ ...EMPTY_API, ...value.rest_api })
      setBl(EMPTY_BL)
    } else {
      setMode(initialMode)
      setBl(EMPTY_BL)
      setApi(EMPTY_API)
    }
  }, [open, value, initialMode])

  const params = api.params || []
  const patchApi = (p: Partial<RestApiValidation>) => setApi((prev) => ({ ...prev, ...p }))

  const valid = mode === 'business_logic'
    ? (bl.method || '').trim().length > 0
    : ((api.url || '').trim().length > 0 || (api.ioflow_api || '').trim().length > 0)

  const handleSave = () => {
    if (!valid) return
    if (mode === 'business_logic') {
      onSave({ mode: 'business_logic', business_logic: { ...bl, method: bl.method.trim() } })
    } else {
      onSave({
        mode: 'rest_api',
        rest_api: {
          ...api,
          url: (api.url || '').trim(),
          ioflow_api: (api.ioflow_api || '').trim() || undefined,
        },
      })
    }
    onClose()
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={title || 'Item Change — Business Logic / Call API'}
      size="default"
      footer={
        <div className="flex items-center justify-end gap-2">
          <button className="btn btn-ghost btn-sm" onClick={onClose}>Cancel</button>
          <button className="btn btn-primary btn-sm" disabled={!valid} onClick={handleSave}>Save</button>
        </div>
      }
    >
      <div>
        <div style={{ display: 'inline-flex', borderRadius: 4, overflow: 'hidden', marginBottom: 4 }}>
          <button type="button" style={{ ...modeBtn(mode === 'business_logic'), borderTopLeftRadius: 4, borderBottomLeftRadius: 4, borderRight: 'none' }}
            onClick={() => setMode('business_logic')}>Business Logic</button>
          <button type="button" style={{ ...modeBtn(mode === 'rest_api'), borderTopRightRadius: 4, borderBottomRightRadius: 4 }}
            onClick={() => setMode('rest_api')}>Call API</button>
        </div>
        <div style={helpStyle}>
          {helpText || (
            <>
              The function / endpoint returns values to set (matched by column name).
              It may also (un)protect columns, fill other forms (header / detail by
              form number) and add multiple rows.
            </>
          )}
        </div>

        {mode === 'business_logic' ? (
          <BusinessLogicFields
            value={bl}
            onChange={setBl}
            methodLabel="Function name"
            onOpenCode={onOpenCode ? () => {
              const m = (bl.method || '').trim()
              if (!m) return
              onSave({ mode: 'business_logic', business_logic: { ...bl, method: m } })
              onClose()
              onOpenCode(m)
            } : undefined}
          />
        ) : (
          <>
            <label style={labelStyle}>Route via IOFlow API</label>
            <select style={{ ...inputStyle, cursor: 'pointer' }} value={api.ioflow_api || ''}
              onChange={(e) => patchApi({ ioflow_api: e.target.value || undefined })}>
              <option value="">— direct URL (below) —</option>
              {ioflowApis.map((n) => <option key={n} value={n}>{n}</option>)}
              {api.ioflow_api && !ioflowApis.includes(api.ioflow_api) && (
                <option value={api.ioflow_api}>{api.ioflow_api} (not in design)</option>
              )}
            </select>
            {api.ioflow_api ? (
              <p style={helpStyle}>
                Sent through IOFlow — the flow subscribed to <code>api.{api.ioflow_api}</code> handles
                it and returns the same contract. URL / method / auth below are ignored.
              </p>
            ) : null}
            <label style={labelStyle}>API URL</label>
            <input style={inputStyle} value={api.url || ''} placeholder="https://api.example.com/item-change"
              onChange={(e) => patchApi({ url: e.target.value })} />
            <div style={rowStyle}>
              <div style={{ flex: 1 }}>
                <label style={labelStyle}>Method</label>
                <select style={{ ...inputStyle, cursor: 'pointer' }} value={api.method || 'POST'}
                  onChange={(e) => patchApi({ method: e.target.value as 'GET' | 'POST' })}>
                  <option value="POST">POST</option>
                  <option value="GET">GET</option>
                </select>
              </div>
              <div style={{ flex: 1 }}>
                <label style={labelStyle}>On error</label>
                <select style={{ ...inputStyle, cursor: 'pointer' }} value={api.on_error || 'allow'}
                  onChange={(e) => patchApi({ on_error: e.target.value as 'allow' | 'block' })}>
                  <option value="allow">Allow (skip the cascade)</option>
                  <option value="block">Block</option>
                </select>
              </div>
            </div>
            <label style={labelStyle}>Auth header name</label>
            <input style={inputStyle} value={api.auth_header || ''} placeholder="Authorization"
              onChange={(e) => patchApi({ auth_header: e.target.value })} />
            <label style={labelStyle}>Auth token (literal or {'{var.NAME}'})</label>
            <input style={inputStyle} value={api.auth_token || ''} placeholder="{var.API_TOKEN}"
              onChange={(e) => patchApi({ auth_token: e.target.value })} />

            <div style={sectionTitleStyle}>Values sent to the API</div>
            {params.map((p, i) => (
              <div key={i} style={{ ...rowStyle, marginTop: 6 }}>
                <input style={{ ...inputStyle, maxWidth: 130 }} value={p.name} placeholder="param name"
                  onChange={(e) => {
                    const next = [...params]; next[i] = { ...p, name: e.target.value }
                    patchApi({ params: next })
                  }} />
                <ParamValueControl source={p.source} value={p.value}
                  formColumnNames={formColumnNames} headerColumnNames={headerColumnNames}
                  onChange={(source, val) => {
                    const next = [...params]; next[i] = { name: p.name, source, value: val } as RestApiParam
                    patchApi({ params: next })
                  }} />
                <button style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--color-error, #cf222e)' }}
                  onClick={() => patchApi({ params: params.filter((_, j) => j !== i) })}>
                  <Trash2 size={14} />
                </button>
              </div>
            ))}
            <button style={addBtnStyle}
              onClick={() => patchApi({ params: [...params, { name: '', source: 'form', value: '' }] })}>
              <Plus size={12} /> Add value
            </button>
            <p style={helpStyle}>
              When no values are configured the whole current row is posted. The
              endpoint returns the item_change contract (a flat {'{col: value}'},
              or {'{ "forms": { "1": {…}, "2": [rows] } }'}).
            </p>
          </>
        )}
      </div>
    </Modal>
  )
}
